import React, { FC, useState } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { ReactSVG } from "react-svg";
import { RootState } from "reducers/rootReducer";

import PaymentsCard from "components/Dashboard/MessageTypes/PaymentsCard";
import EventsCard from "components/Dashboard/MessageTypes/EventsCard";
import RsvpCard from "components/Dashboard/MessageTypes/RsvpCard";
import SurveysCard from "components/Dashboard/MessageTypes/SurveysCard";
import AlertsCard from "components/Dashboard/MessageTypes/AlertsCard";
import FilesCard from "components/Dashboard/MessageTypes/FilesCard";
import PeopleCard from "components/Dashboard/MessageTypes/PeopleCard";
import MediaAiCard from "components/Dashboard/MessageTypes/MediaAiCard";
import CamerasCard from "components/Dashboard/MessageTypes/CamerasCard";
import RxSensorCard from "components/Dashboard/MessageTypes/RxSensorCard";
import MediaGallery from "components/Dashboard/MessagePanel/MediaGallery";
import BeamIcon from "components/Dashboard/MessageTypes/BeamIcon";
import PageFilterBar from "components/Dashboard/MessageTypes/PageFilterBar";
import {
  toAlerts,
  toCameraAlerts,
  toCameras,
  toEvents,
  toFiles,
  toMediaAiAnalyses,
  toPayments,
  toPeopleFromMembers,
  toRsvp,
  toSensorDevices,
  toSurveys,
} from "components/Dashboard/MessageTypes/adapters";
import AddEventModal from "components/Modals/AddEventModal";
import AddPayModal from "components/Modals/AddPayModal";
import IoTAlertModal from "components/Modals/IoTAlertModal";
import useRawMediaUpload from "hooks/useRawMediaUpload";
import { PAGE_TYPES } from "utils/PageTypes";
import { PAGE_TYPE_BUCKETS, resolveActiveBucket } from "utils/messageBuckets";
import "components/Dashboard/MessageTypes/MessageTypes.scss";

/** Matches MediaGallery: a file tile hands its URL straight to the browser. */
const openFileUrl = (url?: string) => {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * The filtered result, shown in the right-hand column where the map normally
 * sits.
 *
 * Filtering to a single type used to swap the message stream in the centre for
 * that type's ledger view. The centre column now always keeps the chat/message
 * result, and these views moved here instead — so a reader can read the stream
 * and its summary side by side rather than one instead of the other.
 *
 * The map column keeps the map for Places, GPS live, GPS reports and no filter;
 * Dashboard decides which of the two mounts, so this component only ever has to
 * render a card view.
 */
const FilteredPanel: FC<{ socket: ISocket; params: DashboardParams }> = ({
  socket,
  params,
}) => {
  const { screens, setScreens } = params;
  const isMobileScreen = useMediaQuery({ query: "(max-width: 700px)" });

  const { selectedItem } = useSelector((state: RootState) => state.search);
  const { allUsers } = useSelector((state: RootState) => state.auth);
  const { messages, messageFilter, messageBucket } = useSelector(
    (state: RootState) => state.message
  );

  const pageId = selectedItem?.pageId;
  const pageMessages = messages?.[pageId] || [];
  const activeBucket = resolveActiveBucket(
    messageBucket || "",
    messageFilter || []
  );

  const { uploadRawFiles } = useRawMediaUpload(selectedItem);

  /**
   * The ledger CTAs open the same modals a stream item would. MessagePanel
   * keeps its own instances for the stream, so this panel carries its own
   * rather than reaching across columns for someone else's state.
   */
  const [eventModalOpen, setEventModalOpen] = useState<any>({ visible: false });
  const [payModalOpen, setPayModalOpen] = useState<any>({ visible: false });
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<any>({});

  /**
   * The cards hand back their own row ids, which are the message ids stringified
   * by the adapters. Resolving one back to its message lets a card CTA reuse the
   * exact modal the stream item would have opened.
   */
  const openMessage = (id: string, modal: "event" | "pay" | "alert") => {
    const message = pageMessages.find(
      (item) => String(item.id) === String(id)
    );
    if (!message) return;
    setSelectedMessage(message);
    if (modal === "event") setEventModalOpen({ visible: true, mode: "edit" });
    if (modal === "pay") setPayModalOpen({ visible: true, mode: "edit" });
    if (modal === "alert") setAlertModalOpen(true);
  };

  const type =
    selectedItem?.type && selectedItem.type === PAGE_TYPES.BROADCAST
      ? "orange"
      : undefined;
  const color = type === "orange" ? "#FF5F25" : "#508AFA";

  const bucketLabel =
    PAGE_TYPE_BUCKETS.find((bucket) => bucket.key === activeBucket)?.label ||
    "Filtered";

  const renderBucketView = () => {
    switch (activeBucket) {
      case "media":
        return (
          <MediaGallery
            messages={pageMessages}
            onFilesSelected={uploadRawFiles}
            canUpload={
              selectedItem?.currentUserRole === "edit" ||
              selectedItem?.currentUserRole === "write"
            }
          />
        );
      case "pay":
        return (
          <PaymentsCard
            payments={toPayments(pageMessages)}
            onOpenInvoice={(payment) => openMessage(payment.id, "pay")}
            onOpenProfile={(payment) => openMessage(payment.id, "pay")}
          />
        );
      case "event":
        return (
          <EventsCard
            events={toEvents(pageMessages)}
            onSelectEvent={(event) => openMessage(event.id, "event")}
          />
        );
      case "rsvp": {
        const rsvp = toRsvp(pageMessages);
        // Who was invited is a page-membership fact the adapter cannot see
        // from the message stream — without it the response rate compares the
        // replies against themselves and is always 0% or 100%.
        const memberCount =
          selectedItem?.persistent?.static?.metadata?.members?.users?.length;
        return (
          <RsvpCard
            eventTitle={rsvp.eventTitle}
            invited={memberCount || rsvp.invited}
            respondents={rsvp.respondents}
          />
        );
      }
      case "files":
        return (
          <FilesCard
            files={toFiles(pageMessages)}
            // Same handling the gallery already gives a file tile: hand it to
            // the browser, which knows whether to render or save it.
            onPreview={(file) => openFileUrl(file.url)}
            onDownload={(file) => openFileUrl(file.url)}
          />
        );
      case "survey":
        return <SurveysCard surveys={toSurveys(pageMessages)} />;
      case "alert":
        return (
          <AlertsCard
            alerts={toAlerts(pageMessages)}
            onViewLog={(alert) => openMessage(alert.id, "alert")}
          />
        );
      case "cam": {
        // The tiles need the alerts to know which camera to lift and flag, so
        // both come off the same stream rather than the card fetching its own.
        const cameras = toCameras(pageMessages);
        return (
          <CamerasCard
            cameras={cameras}
            alerts={toCameraAlerts(pageMessages, cameras)}
            pageId={pageId}
            socket={socket}
          />
        );
      }
      case "rx":
        return (
          <RxSensorCard devices={toSensorDevices(pageMessages)} socket={socket} />
        );
      case "people":
        return (
          <PeopleCard
            people={toPeopleFromMembers(
              selectedItem?.persistent?.static?.metadata?.members?.users,
              allUsers
            )}
          />
        );
      case "ai": {
        const analyses = toMediaAiAnalyses(pageMessages);
        // No recognition service writes to these messages yet. An empty list is
        // the truthful answer; a queued card per media would imply work that
        // nothing is doing.
        if (!analyses.length) {
          return (
            <div className="mt-empty">
              No AI analysis yet. Visual and auditory recognition results appear
              here once the analysis service is enabled for this page.
            </div>
          );
        }
        return (
          <div className="mt-ai-stack">
            {analyses.map((analysis) => (
              <MediaAiCard key={analysis.mediaId} analysis={analysis} />
            ))}
          </div>
        );
      }
      // A hand-picked combination from the funnel matches no single pill, so
      // there is no one ledger to show for it. Saying so beats a blank column.
      default:
        return (
          <div className="mt-empty">
            This combination of message types has no single summary view. Pick
            one type from the filter bar to see its view here.
          </div>
        );
    }
  };

  return (
    <div className="main-column filtered-panel mr-4">
      <div className="main-content">
        <div className="main-header">
          <div className="d-flex align-items-center">
            <BeamIcon name="filter" size={22} />
            <h3 className="ml-3" style={{ color }}>
              {bucketLabel}
            </h3>
          </div>
          <div className="d-flex">
            <ReactSVG
              src="/images/expand-icon.svg"
              className="close-icon mr-4"
              onClick={() =>
                setScreens({
                  ...screens,
                  active: screens.active ? "" : "map",
                })
              }
            />
            {!isMobileScreen && (
              <ReactSVG
                src="/images/close-icon.svg"
                className="close-icon"
                onClick={() => setScreens({ ...screens, map: false })}
              />
            )}
          </div>
        </div>

        {/*
          Same bar, same place as the map panel's: this panel only exists
          because a pill was clicked, so the row that did it has to still be
          here for the reader to pick another bucket or go back to Map.
        */}
        <PageFilterBar />

        <div className="filtered-panel__body">{renderBucketView()}</div>
      </div>

      {eventModalOpen?.visible && (
        <AddEventModal
          onClose={() => setEventModalOpen({ visible: false })}
          eventObj={selectedMessage?.data}
          mode={eventModalOpen?.mode}
          id={`${selectedMessage?.id}${localStorage.getItem("RID")}`}
          socket={socket}
          message={selectedMessage}
        />
      )}

      {payModalOpen?.visible && (
        <AddPayModal
          onClose={() => setPayModalOpen({ visible: false })}
          eventObj={selectedMessage?.data}
          mode={payModalOpen?.mode}
          id={`${selectedMessage?.id}${localStorage.getItem("RID")}`}
          socket={socket}
          message={selectedMessage}
        />
      )}

      {alertModalOpen && (
        <IoTAlertModal
          onClose={() => setAlertModalOpen(false)}
          alertObj={selectedMessage?.data}
        />
      )}
    </div>
  );
};

export default FilteredPanel;
